import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";

export type Prov = { key: string; label: string; min: number; max: number; payments: number; test_mode: boolean };

export function useInstalments(): Prov[] {
  const { user } = useAuth() as any;
  const [list, setList] = useState<Prov[]>([]);
  useEffect(() => { api.get("/instalments/config").then((r) => setList(r.data.providers || [])).catch(() => setList([])); }, [user?.id]);
  return list;
}

const fits = (p: Prov, price: number) => price >= p.min && price <= p.max;

export function InstalmentCue({ price, testId = "instalment-cue" }: { price: number; testId?: string }) {
  const ok = useInstalments().filter((p) => fits(p, price));
  if (!ok.length || !price) return null;
  return (
    <p className="text-xs text-neutral-600 mt-1" data-testid={testId}>
      or {ok[0].payments} interest-free payments of <span className="font-medium text-neutral-900" data-testid={`${testId}-amount`}>{money(Math.round((price / ok[0].payments) * 100) / 100)}</span> with{" "}
      {ok.map((p, i) => <React.Fragment key={p.key}>{i > 0 && " or "}<span className="font-medium text-neutral-900">{p.label}</span></React.Fragment>)}
      {ok.some((p) => p.test_mode) && <span className="ml-2 text-[10px] uppercase tracking-wider text-amber-700" data-testid={`${testId}-test-badge`}>Test mode · admins only</span>}
    </p>
  );
}

export function InstalmentCheckout({ form, formRef }: { form: any; formRef: React.RefObject<HTMLFormElement> }) {
  const { payload, promo, rewards, quote, ship } = useCart();
  const list = useInstalments();
  const [busy, setBusy] = useState("");
  if (!list.length || !quote || quote.total <= 0) return null;
  const go = async (p: Prov) => {
    if (formRef.current && !formRef.current.reportValidity()) return;
    setBusy(p.key);
    try {
      const r = await api.post(`/instalments/${p.key}/checkout`, { ...form, ...ship, items: payload(), promo_code: promo || null, rewards: rewards || null, origin_url: window.location.origin });
      window.location.href = r.data.redirect_url;
    } catch (e) { toast.error(errMsg(e)); setBusy(""); }
  };
  return (
    <div className="space-y-2" data-testid="instalment-checkout">
      <div className="flex items-center gap-4 my-2 text-[11px] tracking-[0.2em] uppercase text-neutral-400"><span className="flex-1 h-px bg-neutral-200" />or pay in instalments<span className="flex-1 h-px bg-neutral-200" /></div>
      {list.map((p) => {
        const ok = fits(p, quote.total);
        return (
          <div key={p.key}>
            <button type="button" disabled={!ok || !!busy || quote.ship_ok === false} onClick={() => go(p)} data-testid={`instalment-pay-${p.key}`}
              className="w-full py-4 text-xs tracking-[0.2em] uppercase border border-neutral-900 bg-white hover:bg-neutral-900 hover:text-white transition-colors disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-neutral-900">
              {busy === p.key ? "Redirecting…" : `Pay in ${p.payments} with ${p.label}`}{ok && <span className="normal-case tracking-normal ml-2 opacity-70">· {money(Math.round((quote.total / p.payments) * 100) / 100)} each</span>}
            </button>
            {!ok && <p className="text-[11px] text-neutral-500 mt-1" data-testid={`instalment-limit-${p.key}`}>{p.label} is available for orders from {money(p.min)} to {money(p.max)}.</p>}
            {p.test_mode && <p className="text-[11px] text-amber-700 mt-1" data-testid={`instalment-test-${p.key}`}>Test mode — no real payment is taken. Only admins can see this option until your {p.label} keys are added.</p>}
          </div>
        );
      })}
    </div>
  );
}
